import Backgrounds, { BackgroundType } from 'app/src/backgrounds';
// eslint-disable-next-line no-restricted-imports
import {
  BackgroundItemState,
  CreateMenuState,
} from 'app/gamma/src/modules/state/ui/create-menu';
// eslint-disable-next-line no-restricted-imports
import { State } from 'app/gamma/src/modules/types';
// eslint-disable-next-line no-restricted-imports
import { isTeamAdmin } from 'app/gamma/src/selectors/members';
// eslint-disable-next-line no-restricted-imports
import {
  getEnterpriseWithPermissions,
  isAdminOfEnterpriseTeams,
} from 'app/gamma/src/selectors/enterprises';
// eslint-disable-next-line no-restricted-imports
import { PermissionLevel } from 'app/gamma/src/types/models';
import { getOrgsWithPermissions, OrgWithPermissions } from './teams';

export const getCreateBoardSelectedTeam = (
  state: State,
  menuState: CreateMenuState,
): OrgWithPermissions | null => {
  if (menuState.selectedTeamId) {
    return (
      getOrgsWithPermissions(state).find(
        (org) => org.model.id === menuState.selectedTeamId,
      ) || null
    );
  }
  return null;
};

export const getCreateBoardDisallowed = (
  state: State,
  menuState: CreateMenuState,
): boolean => {
  const selectedTeam = getCreateBoardSelectedTeam(state, menuState);
  let isAdmin = false;
  let boardRestrictions;
  if (selectedTeam) {
    isAdmin = isTeamAdmin(state, selectedTeam.model);
    boardRestrictions =
      selectedTeam.model.prefs &&
      selectedTeam.model.prefs.boardVisibilityRestrict &&
      Object.keys(selectedTeam.model.prefs.boardVisibilityRestrict)
        .filter(
          (key) => key !== 'enterprise' || !!selectedTeam.model.idEnterprise,
        )
        .map(
          (key) =>
            selectedTeam.model.prefs?.boardVisibilityRestrict?.[
              key as PermissionLevel
            ],
        );
  } else {
    const enterprise = getEnterpriseWithPermissions(state);
    isAdmin = !!enterprise && isAdminOfEnterpriseTeams(state, enterprise.model);
    boardRestrictions =
      enterprise &&
      enterprise.model.organizationPrefs &&
      enterprise.model.organizationPrefs.boardVisibilityRestrict &&
      ['private', 'public'].map(
        (key) =>
          enterprise.model.organizationPrefs?.boardVisibilityRestrict?.[
            key as PermissionLevel
          ] ?? 'org',
      );
  }

  // this is true if board creation for all visibility types is set to 'none'
  const allDisallowed =
    !!boardRestrictions && boardRestrictions.every((val) => val === 'none');

  // this is true if board creation for none of the visibility types is set to 'org'
  const onlyAdminsAllowed =
    !!boardRestrictions && boardRestrictions.every((val) => val !== 'org');

  return allDisallowed || (!isAdmin && onlyAdminsAllowed);
};

export const getCreateBoardMenuBackgroundStyle = (
  state: CreateMenuState,
): { backgroundColor: string; backgroundImage: string | undefined } => {
  const photos = state.photos;
  const { selected, preSelected } = state.background;
  const bgData: BackgroundItemState = {
    ...(selected.id ? selected : preSelected),
  };

  if (bgData.type === BackgroundType.Unsplash) {
    const photo = photos.find(({ id }) => id === bgData.id);

    return {
      backgroundColor: Backgrounds.colors.blue.color,
      backgroundImage: photo && `url(${photo.urls.small})`,
    };
  }

  if (bgData.type === BackgroundType.Gradient) {
    const gradient = Backgrounds.gradients[bgData.id!];

    return {
      backgroundColor: gradient.color,
      backgroundImage: `url(${gradient.path})`,
    };
  }

  return {
    backgroundColor: Backgrounds.colors[bgData.id!].color,
    backgroundImage: undefined,
  };
};

export const getCreateBoardSelectedVisibility = (
  state: State,
  menuState: CreateMenuState,
): PermissionLevel | null => {
  const { selectedTeamId, selectedVisibility } = menuState;
  const getDefaultVisibility = (
    allowedVis: PermissionLevel[],
    level: PermissionLevel,
  ) => {
    if (allowedVis.includes(level)) {
      return level;
    } else if (allowedVis.includes('enterprise')) {
      return 'enterprise';
    } else {
      return allowedVis[0] || null;
    }
  };
  const team =
    !!selectedTeamId &&
    getOrgsWithPermissions(state).find(
      (org) => org.model.id === selectedTeamId,
    );

  const entOrOrg = team ? team : getEnterpriseWithPermissions(state);

  if (entOrOrg) {
    if (selectedVisibility) {
      if (!entOrOrg.allowedVis.includes(selectedVisibility)) {
        return getDefaultVisibility(entOrOrg.allowedVis, 'private');
      }

      return selectedVisibility;
    }

    return getDefaultVisibility(entOrOrg.allowedVis, 'org');
  }

  return selectedVisibility === 'public' ? selectedVisibility : 'private';
};
